/**
 * @file inward-camera.tsx
 * @module app/modules/inward/components/inward-camera
 * @description In-app camera for Inward approvals: opens the device camera (back camera on
 *   the tablet), shows a live preview and adds one JPEG per "Capture" tap. Several photos can
 *   be taken before closing. There is no gallery option. If the browser cannot open the camera
 *   (no permission, no camera, not HTTPS), the reason is shown.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";

interface InwardCameraProps {
  show: boolean;
  title: string;
  /** file name prefix, e.g. "warehouse" -> warehouse-1712345678901.jpg */
  prefix: string;
  onCapture: (file: File) => void;
  onClose: () => void;
}

/** Why the camera could not be opened, in words a user can act on. */
export const cameraErrorMessage = (err: unknown): string => {
  const name = (err as { name?: string })?.name;
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "Camera permission was denied. Allow camera access for this site in the browser settings, then try again.";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return "No camera was found on this device.";
  }
  if (name === "NotReadableError") {
    return "The camera is being used by another app. Close it and try again.";
  }
  return "The camera could not be opened on this device.";
};

/** Draws the current video frame into a JPEG file. */
export const captureFrame = (
  video: HTMLVideoElement,
  prefix: string,
  now: number = Date.now()
): Promise<File | null> => {
  const width = video.videoWidth || 1280;
  const height = video.videoHeight || 720;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.resolve(null);
  ctx.drawImage(video, 0, 0, width, height);
  return new Promise((resolve) =>
    canvas.toBlob(
      (blob) =>
        resolve(
          blob
            ? new File([blob], `${prefix}-${now}.jpg`, { type: "image/jpeg" })
            : null
        ),
      "image/jpeg",
      0.9
    )
  );
};

const InwardCamera = ({
  show,
  title,
  prefix,
  onCapture,
  onClose,
}: InwardCameraProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [problem, setProblem] = useState("");
  const [ready, setReady] = useState(false);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState(false);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setReady(false);
  }, []);

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setProblem("");
    setCount(0);
    const media = navigator.mediaDevices;
    if (!media?.getUserMedia) {
      setProblem(
        "This browser cannot open the camera. Use Chrome on the tablet (the site must be opened over HTTPS)."
      );
      return undefined;
    }
    media
      .getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          const played = videoRef.current.play?.();
          if (played && typeof played.catch === "function")
            played.catch(() => undefined);
        }
        setReady(true);
      })
      .catch((err) => {
        if (!cancelled) setProblem(cameraErrorMessage(err));
      });
    return () => {
      cancelled = true;
      stop();
    };
  }, [show, stop]);

  const capture = async () => {
    if (!videoRef.current) return;
    setBusy(true);
    const file = await captureFrame(videoRef.current, prefix);
    setBusy(false);
    if (file) {
      onCapture(file);
      setCount((n) => n + 1);
    } else {
      setProblem("The photo could not be taken. Try again.");
    }
  };

  const close = () => {
    stop();
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={close}
      size="lg"
      centered
      data-testid="inward-camera"
    >
      <Modal.Header closeButton>
        <Modal.Title className="fs-20">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {problem ? (
          <div
            className="alert alert-danger mb-0 fs-15"
            data-testid="inward-camera-error"
          >
            {problem}
          </div>
        ) : (
          <video
            ref={videoRef}
            className="inward-camera-video"
            autoPlay
            playsInline
            muted
            data-testid="inward-camera-video"
          />
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <span className="fs-15" data-testid="inward-camera-count">
          {count} photo{count === 1 ? "" : "s"} taken
        </span>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            className="inward-touch-btn"
            onClick={close}
          >
            Done
          </Button>
          <Button
            variant="primary"
            className="inward-touch-btn px-8"
            disabled={!ready || busy || !!problem}
            onClick={capture}
            data-testid="inward-camera-capture"
          >
            {busy ? "Saving…" : "Capture"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default InwardCamera;
